<script lang="ts" setup>
// GraphiqueSeriesEnedis (DEV-1508 lot 2) — UN graphe : un sens, une unité, les
// séries qui la partagent, et le geste qui recadre la vue.
//
// Trois choses que ce composant tient, et qu'aucun autre ne peut tenir :
//
//  • L'UNITÉ EST DANS L'AXE. Le regroupement par unité est fait en amont
//    (`graphesEnedis.ts`) ; ici l'unité est ÉCRITE sur l'axe des ordonnées. Des
//    kVA et des kW ne peuvent pas se retrouver sur la même échelle, et quand une
//    unité n'est pas connue, l'axe le dit plutôt que de laisser croire.
//
//  • UN TROU RESTE UN TROU. `connectgaps: false` : un `null` fait un blanc dans
//    la courbe, jamais un point à zéro, jamais un segment tiré par dessus.
//
//  • PAS DE RANGESLIDER, bien qu'une figure servie par l'API de graphes en
//    porte un. Écart assumé : un glisser de curseur émettrait des bornes d'axe
//    en rafale, que la garde ci-dessous prendrait pour autant de gestes de
//    recadrage. La bande de complétude, au-dessus, tient ce rôle.
//
//  • LE RECADRAGE SE FAIT AU CLIQUER-GLISSER. `dragmode: "zoom"` sur l'axe des
//    temps ; le glisser remonte en `brush`, le double-clic en `retour`. Aucun
//    bouton Zoomer, aucune fenêtre préréglée (addendum du 09/09).
//
//  • L'AXE DES TEMPS EST CELUI DE LA FENÊTRE REGARDÉE. Le graphe ne laisse pas
//    Plotly choisir ses bornes : il POSE `xaxis.range` sur la fenêtre que la
//    page regarde. Sans cela, un brush du 05/12 au 03/03 chargeait bien
//    l'heure, mais la peignait sur l'axe du contexte entier — un cinquième de
//    la largeur pour cinq fois moins de temps (staging, 10/09). Et le range
//    posé n'est PAS un geste : quand Plotly le renvoie en `plotly_relayout`
//    après un redessin, il est reconnu et ignoré, sinon le retour au contexte
//    repartait en chargement.
//
// Une série que le compteur ne publie pas n'est pas tracée : elle est écrite en
// mention, à sa place. UNE SÉRIE SANS AUCUNE MESURE SUR LA FENÊTRE REGARDÉE non
// plus (correctif 5a-2, défaut vu en prod le 15/09) : elle prenait une entrée de
// légende et une couleur de la palette pour ne rien dessiner. Elle se dit en
// pied, avec la pastille de l'absence, et revient dès que la fenêtre la
// remplit — sans changer de couleur, qui tient à sa clé et non à sa place.
// Une ligne de zéros se lirait comme une mesure. Une
// statistique OMISE parce que la fenêtre dépasse celle de son calcul se dit de
// la même façon, dans la légende, là où sa courbe aurait été : sans elle,
// l'écran serait celui d'un compteur qui ne publie rien.
import { computed } from "vue"
// Imports EXPLICITES, et non l'auto-import de l'application : `vitest.config.ts`
// ne charge que le plugin `vue()`, donc un composant seulement auto-importé
// serait rendu comme élément inconnu et ses slots nommés ne seraient jamais
// rendus. Voir `monterEnedis.spec.helper.ts`.
import PlotlyChartCard from "@modules/data/components/data/chart/PlotlyChartCard.vue"
import DataChartLegendKPI from "@modules/data/components/data/chart/DataChartLegendKPI.vue"
import type { DataChartLegendKpiItem } from "@modules/data/components/data/chart/dataChartLegendKpi.types"
import DataOrigineBadge from "@modules/data/components/data/enedis/DataOrigineBadge.vue"
import {
    formateNombre,
    texteDeLaSomme,
} from "@modules/data/utils/tableauValeurs"
import {
    HOMEYS_PLOTLY_COLORWAY,
    buildTooltipChartTemplate,
    finalizeHomeysPlotlyGraph,
} from "@modules/data/utils/plotly"
import { nomDuFichierDeGraphe } from "@modules/data/utils/nomFichierGraphe"
import { libelleDuPas } from "@modules/data/utils/pasDeLaCarte"
import type {
    GrainSeries,
    StatSeries,
} from "@modules/data/services/sourceDataEnedis.service"
import {
    MENTION_AUCUNE_MESURE,
    MENTION_NON_PUBLIEE,
    PHRASE_AUCUNE_MESURE,
    PHRASE_AUCUNE_PERIODE,
    bornesDeLAxeY,
    complementOrigineEnveloppe,
    estVideSurLaFenetre,
    dernierPointFini,
    familleDuGraphe,
    empriseDuPoint,
    estUniteDeDuree,
    heuresLisibles,
    texteDuTotalDeLaFenetre,
    AUCUNE_VALEUR_SERVIE,
    bornesDuPoint,
    finCouverteDuPoint,
    JOUR_MS,
    phraseCarteSansValeur,
    serieNonServie,
    groupesDEnveloppe,
    libelleDeLEnveloppe,
    pointDansLaFenetre,
    type GrapheEnedis,
    type GroupeEnveloppe,
    type SerieTracee,
} from "@modules/data/utils/graphesEnedis"
import {
    LIBELLES_DIRECTION,
    LIBELLES_ORIGINE,
    type OrigineSerie,
} from "@modules/data/utils/ongletsEnedis"
import type { DirectionEnedis } from "@modules/data/services/sourceDataEnedis.service"
import {
    libelleDeLaPart,
    type PartOrigine,
} from "@modules/data/utils/origineEnedis"
import { jourDe } from "@modules/data/utils/navigationEnedis"
import { dateFr } from "@modules/data/utils/fraicheurEnedis"
import DataCompletudeLegend from "@modules/data/components/data/completude/DataCompletudeLegend.vue"
import {
    ETATS_COMPLETUDE,
    ORDRE_LEGENDE,
    type EtatCompletude,
} from "@modules/data/utils/completude"
import type { Fenetre } from "@modules/data/composables/useSeriesEnedis"

/** Un jour que la bande de complétude ne compte PAS comme mesuré, et son état. */
export type JourAbsent = { jour: string; etat: EtatCompletude }

const props = withDefaults(
    defineProps<{
        graphe: GrapheEnedis
        rendu?: "barres" | "courbe"
        hauteur?: number
        /**
         * Hauteur minimale de la zone de contenu de la carte. La vue la
         * resserre sous 768 px (item 27c) ; sans elle, la carte garde les
         * 20 rem d'hier.
         */
        hauteurMinimale?: string
        /** La fenêtre REGARDÉE. Elle devient l'axe des temps, telle quelle. */
        fenetre?: Fenetre | null
        /**
         * LE CONTEXTE CHARGÉ (lot 5g, annexe de la revue du lot 5h).
         *
         * Il BORNE la largeur des barres : un mois daté par son premier jour
         * alors que le contexte n'en couvre qu'une partie débordait de ce qui a
         * été chargé, des deux côtés. `null` = rien n'est borné, exactement
         * comme avant.
         */
        contexte?: Fenetre | null
        /**
         * Le grain SERVI. Il décide du format des dates : une heure se lit à
         * l'heure, un jour se lit au jour. Sans lui, l'axe retombe sur le jour.
         */
        grain?: GrainSeries | null
        /**
         * Les clés des séries de l'ONGLET, dans leur ordre stable. La couleur
         * d'une série est sa place ici, pas sa place dans les traces du graphe :
         * une grandeur garde sa couleur quand une série voisine n'a rien à
         * tracer sur la fenêtre resserrée, et d'un onglet à l'autre.
         */
        clesDeLOnglet?: string[]
        /** Le numéro du point, pour nommer le fichier de l'image exportée. */
        prm?: string | null
        /**
         * La grandeur telle qu'elle se LIT, pour nommer le fichier de l'image :
         * le libellé de l'onglet, jamais une clé technique (marque blanche).
         */
        cleOngletLisible?: string
        /** Vrai tant que la lecture de l'onglet est en vol. */
        chargement?: boolean
        /**
         * Vrai quand la lecture a ÉCHOUÉ. À ne jamais confondre avec une
         * grandeur que le compteur ne publie pas : les deux donnent un écran
         * sans courbe, et ce ne sont pas les mêmes faits (18ᵉ forme de garde
         * qui ment, `top/docs/methode-verification-donnees.md` §12).
         */
        echecDeLecture?: boolean
        /**
         * LES JOURS SANS MESURE DE LA FENÊTRE, avec le mot de leur état, tels
         * que la bande de complétude les connaît (DEV-1508 lot B1).
         *
         * La règle de Mathieu du 08/09 : un trou se représente DANS la donnée, à
         * sa date, pas par une bannière. La bande, au-dessus, hachurait déjà ces
         * jours ; la figure, elle, restait blanche et muette dessus — un lecteur
         * pouvait lire cette zone blanche comme « rien à consommer » plutôt que
         * comme « rien de collecté ».
         *
         * La vue les borne à la fenêtre regardée et écarte les jours mesurés :
         * ici on ne peint QUE ce qui est reçu, on n'invente aucun jour.
         */
        joursAbsents?: JourAbsent[]
        /**
         * Vrai quand le contexte dépasse ce que la complétude sait détailler
         * jour par jour (`PLAFOND_JOURS`). Dans ce seul cas `joursAbsents` est
         * vide sans que cela veuille dire « aucun trou » : on ne sait pas, donc
         * on ne peint pas, ET on le dit. Ne jamais confondre avec l'attente du
         * verdict, qui n'est pas un fait sur la donnée.
         */
        contexteNonDetaillable?: boolean
        /**
         * Vrai quand les séries de CE graphe portent une grandeur qui
         * s'additionne : la carte écrit alors, en tête de son pied, le total de
         * chaque série sur la fenêtre regardée (lot B4).
         *
         * La vue la calcule avec `estSommableSurLaFenetre`, qui prend l'onglet
         * ET l'unité : une puissance, un index et une durée de dépassement n'ont
         * pas de total à montrer.
         */
        sommable?: boolean
        /**
         * LE NIVEAU SERVI, ÉCRIT SUR LA CARTE (lot 5d, écart T4, décision Q8).
         *
         * La barre de contexte le dit déjà à côté de la fenêtre ; la carte
         * Puissance le redit parce que c'est LÀ qu'il change ce qu'on voit :
         * une courbe native, une enveloppe horaire et une enveloppe journalière
         * ne se lisent pas de la même façon. `null` = rien n'est écrit.
         */
        niveauLisible?: string | null
        /**
         * L'AXE DES ORDONNÉES BORNÉ AUX VALEURS, ET NON À ZÉRO (écart G9).
         *
         * Vrai pour un INDEX, et pour lui seul aujourd'hui : c'est un cumul dont
         * seule la pente se lit. Partout ailleurs le zéro reste l'origine de la
         * lecture, et le retirer laisserait croire à une variation énorme sur un
         * écart de rien.
         */
        axeAuxValeurs?: boolean
        /**
         * LA DERNIÈRE VALEUR QUE LA FAMILLE DÉTIENT, en français (lot 5d4).
         *
         * Elle vient des bornes servies pour CETTE famille, jamais de l'agrégat
         * du compteur. Elle n'est écrite que sur une carte qui n'a aucune valeur
         * à montrer : elle est alors le seul fait qui apprenne au lecteur que ce
         * n'est pas la fenêtre qui est en cause. `null` = non servie, et rien ne
         * s'écrit à sa place.
         */
        derniereValeurDeLaFamille?: string | null
        /**
         * LA DATE QUE PORTE LA LIGNE DE L'ONGLET, en ISO (lot 5d4b).
         *
         * DÉFAUT RELEVÉ en prod le 16/09 sur le C4 `638f1080`, onglet Puissance
         * à 92 jours : la ligne de l'onglet disait « dernière valeur le
         * 15/09/2026 · à jour » — vrai pour la courbe — pendant que les cartes
         * « Puissance réactive totale » et « Puissance apparente » montraient un
         * trait qui s'arrête le 09/08 SANS UN MOT. Un onglet porte plusieurs
         * familles ; une seule date ne parle pas pour toutes.
         *
         * Quand la dernière valeur de CETTE carte est antérieure, elle écrit sa
         * propre date sous son titre. Quand elle est alignée, elle se tait :
         * répéter la même date deux fois n'apprend rien.
         */
        derniereValeurDeLOnglet?: string | null
        /**
         * LE BLOC DES TOTAUX PORTE UNE STATISTIQUE PAR CLASSE ET UN TOTAL
         * (décision Q25 du 17/09, « oui, comme une stat »).
         *
         * Vrai sur l'onglet Dépassements, que la vue tranche : une classe sans
         * aucune valeur servie sur la fenêtre y est NOMMÉE (« aucune valeur »)
         * au lieu de sortir du bloc, et une ligne « Total » ferme la liste.
         * Ailleurs, le bloc reste celui du lot B4 : les séries sans valeur en
         * sortent, et il n'y a pas de total des totaux.
         */
        totalDesClasses?: boolean
        /**
         * LE PAS DE LA COURBE DU COMPTEUR, EN MINUTES, tel que le référentiel
         * le sert (R4). Il ne sert qu'à dire de quoi une enveloppe calculée est
         * calculée. `null` = non lu, et la phrase s'arrête avant le chiffre.
         */
        minutesPasCompteur?: number | null
        /**
         * LES PAS QUE LA VUE OFFRE SUR CETTE CARTE (R1/R7), du plus grossier au
         * plus fin. La carte ne les CALCULE pas : le plafond dapi, ce que le
         * compteur détient et la largeur regardée sont des faits de la page
         * (`utils/pasDeLaCarte.ts`). Vide ou à un seul élément = pas de
         * sélecteur : un choix unique ne se montre pas comme un choix.
         */
        pasPermis?: GrainSeries[]
        /** Le pas RÉELLEMENT servi par les points de cette carte. */
        pasServi?: GrainSeries | null
        /**
         * POURQUOI LE PAS CHOISI N'EST PAS CELUI QUI EST SERVI, quand un
         * recadrage l'a mis hors de portée. Il s'écrit DANS la carte, jamais en
         * bannière (règle de Mathieu du 08/09). `null` = rien à expliquer.
         */
        raisonDuPas?: string | null
    }>(),
    {
        rendu: "courbe",
        hauteur: 300,
        hauteurMinimale: "15rem",
        fenetre: null,
        grain: null,
        clesDeLOnglet: () => [],
        prm: null,
        cleOngletLisible: "",
        chargement: false,
        echecDeLecture: false,
        joursAbsents: () => [],
        contexte: null,
        contexteNonDetaillable: false,
        sommable: false,
        niveauLisible: null,
        axeAuxValeurs: false,
        derniereValeurDeLaFamille: null,
        derniereValeurDeLOnglet: null,
        totalDesClasses: false,
        minutesPasCompteur: null,
        pasPermis: () => [],
        pasServi: null,
        raisonDuPas: null,
    }
)

/**
 * LE FORMAT DES VALEURS, sur l'axe comme dans l'infobulle.
 *
 * Une décimale partout — c'est la règle D4 du brief de design — SAUF sur un axe
 * borné aux valeurs. Un index se compte en unités entières : « 2 875 937,4 kWh »
 * affiche un dixième de kilowattheure qu'aucun compteur ne relève, et fait une
 * graduation deux fois trop large pour la place qu'elle a.
 */
const formatValeur = computed(() => (props.axeAuxValeurs ? ",.0f" : ".1f"))

const emit = defineEmits<{
    /** Le cliquer-glisser : les deux bornes de l'axe des temps. */
    (e: "brush", bornes: { debut: string; fin: string }): void
    /** Le double-clic : retour au contexte. */
    (e: "retour"): void
    /** Le pas choisi sur cette carte. Jamais émis pour le pas déjà servi. */
    (e: "pas", pas: GrainSeries): void
}>()

/**
 * LES SÉRIES QUI ONT UNE COURBE À MONTRER.
 *
 * Deux façons de n'en pas avoir, et elles ne se confondent jamais : la grandeur
 * n'est pas publiée par le compteur, ou elle l'est et aucune de ses mesures ne
 * tombe dans la FENÊTRE REGARDÉE. Ni l'une ni l'autre n'est remise au moteur de
 * tracé : une série sans un seul point fini prenait une entrée de légende et une
 * couleur de la palette pour ne rien dessiner (vu en prod le 15/09, « Énergie
 * réactive » de deux compteurs réels, 365 points, zéro fini).
 *
 * Le filtre suit la fenêtre : resserrer peut vider une série, élargir la
 * ramène. Sa couleur, elle, ne bouge pas — elle est attachée à sa clé dans
 * l'onglet, jamais à sa place dans les traces (D9).
 */
const tracables = computed(() =>
    props.graphe.series.filter(
        (s) => !s.nonPubliee && !estVideSurLaFenetre(s, props.fenetre)
    )
)

/**
 * LES SÉRIES SANS AUCUNE MESURE SUR LA FENÊTRE. Elles sortent de la figure, et
 * se disent en pied de carte avec la pastille de l'absence : un lecteur doit
 * savoir que la grandeur existe et que c'est la fenêtre qui est vide, sinon il
 * cherche une courbe qu'aucun écran ne lui montrera.
 */
const videsSurLaFenetre = computed(() =>
    props.graphe.series.filter((s) => estVideSurLaFenetre(s, props.fenetre))
)

/** Vrai pour une série sans mesure sur la fenêtre, au gabarit. */
function estVide(serie: SerieTracee): boolean {
    return estVideSurLaFenetre(serie, props.fenetre)
}

/**
 * LES SÉRIES QUI DEMANDENT UNE PRÉCISION, et elles seules (décision D3 du brief
 * de design v1.1).
 *
 * La légende de Plotly nomme déjà chaque série, juste au-dessus. Répéter les
 * sept noms sous la figure, dont quatre ne disaient rien de plus, c'était payer
 * une deuxième légende pour rien. Ne restent ici que les séries dont l'origine
 * n'est PAS homogène (« Mesuré 92 % » à côté de « Calculé 8 % » dit une vérité
 * que le mot seul cacherait), celles que le compteur ne publie pas, et celles
 * qui portent une précision sur ce qu'elles sont.
 *
 * Une série entièrement mesurée n'a plus de ligne : son origine se lit dans la
 * pastille d'entête de la carte, au survol du point, et dans le tableau.
 */
const seriesAPreciser = computed(() => {
    // UN NOM DÉJÀ PEINT NE SE DIT PAS ABSENT (lot 5d3). Le même compteur nomme
    // sa classe de pointe `P` dans ses index et ses durées de dépassement,
    // `Pointe` dans ses périodes de facturation ; la page demande donc les deux
    // noms, et le lecteur n'en valorise qu'un. Les deux portent le MÊME libellé
    // client : sans ce filtre, « Durée de dépassement heures de pointe : aucune
    // mesure sur cette fenêtre » s'écrivait sous la courbe des heures de
    // pointe, dessinée juste au-dessus. Deux phrases contradictoires sur un
    // même écran, et c'est la vraie qui perdait.
    const peints = new Set(tracables.value.map((s) => s.libelle))
    // ET QUAND LA CARTE DIT DÉJÀ « aucune période close », elle le dit UNE
    // fois. Répéter « aucune mesure sur cette fenêtre » sous chaque classe
    // dirait la même chose avec le mot d'un autre grain : une période close
    // n'est pas une mesure manquante, c'est un cycle qui n'a pas fini.
    //
    // ET UNE STATISTIQUE QUE DAPI NE SERT PAS AU GRAIN AFFICHÉ N'EST PAS UNE
    // ABSENCE (lot 5d4, relevé le 16/09 sur le C4 réel `638f1080`). La famille
    // « Puissance réactive totale » déclare min, moyenne et maximum ; au grain
    // horaire, seule la moyenne revient. Écrire « (minimum) · Aucune mesure sur
    // cette fenêtre » sous la courbe de la moyenne accusait la fenêtre d'un vide
    // qui vient du grain : resserrer ou élargir n'y aurait rien changé.
    //
    // La règle : une série qui n'a AUCUN point fini sur tout ce qui est chargé
    // ne se dit pas absente de la fenêtre. Quand c'est toute la carte, la carte
    // le dit une fois, avec la date de la famille ; quand ce n'est qu'une
    // statistique, elle ne se liste pas du tout — rien ne se fabrique.
    // ⚠️ UNE FAMILLE FIGÉE NE FAIT PLUS TAIRE LES BADGES (Q21, 17/09) : le
    // graphe reste dessiné, et chaque classe dit sur la figure qu'elle n'a
    // aucune mesure sur cette fenêtre. Le lot 5d4 les supprimait parce qu'il
    // n'y avait plus de figure sous laquelle les écrire.
    const ditParLaCarte =
        props.grain === "periode" && tracables.value.length === 0
    return props.graphe.series.filter(
        (s) =>
            !serieNonServie(s) &&
            !(estVide(s) && (ditParLaCarte || peints.has(s.libelle))) &&
            (s.nonPubliee ||
                estVide(s) ||
                s.mention ||
                s.origines.homogene === null)
    )
})

/**
 * L'ORIGINE DU GRAPHE ENTIER, quand toutes ses séries traçables s'accordent.
 *
 * Elle est écrite une fois, en entête de carte, sans pourcentage : un graphe
 * homogène est à 100 %, et l'écrire serait du bruit. Dès qu'une série diverge,
 * cette pastille disparaît et les parts se lisent série par série, en pied de
 * carte : une origine dominante affichée sur un graphe mixte serait un résumé
 * faux.
 */
const origineDuGraphe = computed<OrigineSerie | null>(() => {
    const t = tracables.value
    if (!t.length) return null
    const o = t[0].origines.homogene
    if (!o) return null
    return t.every((s) => s.origines.homogene === o) ? o : null
})

/**
 * CE QUE « CALCULÉ » NE DISAIT PAS (R4) : de quoi la valeur est calculée.
 *
 * Il se lit APRÈS la pastille, et pas dedans : la pastille est une puce courte,
 * la même partout sur la page, et y loger une phrase de sept mots l'aurait
 * étirée sur toute la largeur du titre. Ensemble, les deux se lisent
 * « Calculé à partir de la courbe de charge au pas de 30 min ».
 */
const complementDeLOrigine = computed(() =>
    complementOrigineEnveloppe({
        series: tracables.value,
        origine: origineDuGraphe.value,
        minutesPasCompteur: props.minutesPasCompteur,
    })
)

/**
 * LE SÉLECTEUR NE S'AFFICHE QU'À PARTIR DE DEUX PAS (R1).
 *
 * Un groupe de boutons à une seule entrée annonce un choix qui n'existe pas :
 * le lecteur presse, rien ne se passe, et il ne sait pas pourquoi. La raison du
 * repli, elle, s'écrit même sans sélecteur — c'est là qu'elle sert le plus.
 */
const pasAOffrir = computed(() =>
    props.pasPermis.length > 1 ? props.pasPermis : []
)

function choisirLePas(pas: GrainSeries): void {
    // Recliquer le pas déjà servi ne coûte pas un appel au lecteur.
    if (pas === props.pasServi) return
    emit("pas", pas)
}

/**
 * LA CARTE N'A PAS DE COURBE À MONTRER : le compteur DÉCLARE des séries, et
 * aucune n'est publiée. C'est la règle d'avant, mot pour mot — un graphe monté
 * sans aucune série déclarée (essai, récit) n'est pas un compteur muet, et sa
 * figure vide reste rendue plutôt que remplacée par une phrase qui affirmerait
 * un fait non lu (charte, art. 3).
 */
const sansCourbe = computed(
    () => tracables.value.length === 0 && props.graphe.series.length > 0
)

/**
 * TOUTES LES SÉRIES SONT VIDES SUR LA FENÊTRE, et le compteur les publie. La
 * carte le dit avec SES mots : « Aucune mesure sur cette fenêtre. » Écrire ici
 * « Ce compteur ne publie pas cette grandeur. » serait faux — la grandeur
 * existe, c'est ce qu'on regarde qui n'en porte pas.
 */
const aucuneMesureSurLaFenetre = computed(
    () => tracables.value.length === 0 && videsSurLaFenetre.value.length > 0
)

/**
 * LA FAMILLE EST FIGÉE AVANT LA FENÊTRE (lot 5d4).
 *
 * C'est un FAIT LU, jamais une impression, et la VUE le tranche : elle ne sert
 * cette date que lorsque la borne déclarée est antérieure au contexte chargé ET
 * qu'aucune valeur finie n'y est arrivée. Une borne qui tombe DANS le contexte
 * ne dit pas jusqu'à quand la famille a servi — sur le C4 `638f1080`, celle du
 * 15/09 compte 576 abscisses toutes nulles (addendum 3).
 */
const familleFigee = computed(() => Boolean(props.derniereValeurDeLaFamille))

/**
 * LA CARTE N'A RIEN À MONTRER, ET CE N'EST PAS LA FENÊTRE (lot 5d4).
 *
 * Le FAIT ne change pas : la famille est figée avant la fenêtre, et la carte le
 * dit d'une phrase portant la date de cette famille. Ce qui change (Q21, 17/09)
 * c'est ce qu'on en tire à l'écran : la phrase ne remplace plus le graphe, elle
 * se pose SOUS LE TITRE et le graphe reste dessiné.
 */
const carteSansValeur = computed(() => sansCourbe.value && familleFigee.value)

/**
 * LE GRAPHE EST TOUJOURS DESSINÉ (décision Q21 du 17/09).
 *
 * « on met toujours les graphes, la carte est une lecture. » Le lot 5d4 avait
 * retiré la figure d'une carte sans aucune valeur sur la fenêtre (le C2 témoin,
 * onglet Dépassements : une phrase seule à la place du graphe). Mathieu
 * renverse : une carte qui perd sa figure perd aussi ses axes, et le lecteur ne
 * sait plus SUR QUOI le vide porte. Le graphe est donc remis dans tous les cas —
 * axes sur la fenêtre regardée, aucune trace quand rien n'est servi, absences
 * peintes comme partout ailleurs (le motif hachuré du lot B1 quand la carte
 * connaît ses jours absents, des axes vides sinon).
 *
 * Une lecture qui a ÉCHOUÉ reste hors de cette règle : on ne sait pas ce que la
 * fenêtre porte, et des axes vides affirmeraient un vide qu'on n'a pas lu.
 */
const figureRemise = computed(() => !props.echecDeLecture)

/** La phrase de la carte sans valeur, avec la date de SA famille quand elle est servie. */
/**
 * LA LIGNE COURTE DE CETTE CARTE, quand sa famille est en retard sur l'onglet.
 *
 * Elle ne porte JAMAIS de verdict : l'agrégat du compteur ne juge que le flux
 * principal, et cette carte n'en est pas. Elle ne porte qu'une date, celle
 * qu'on peut lire dans les points chargés.
 */
const fraicheurDeLaCarte = computed(() => {
    const onglet = props.derniereValeurDeLOnglet
    if (!onglet) return null
    const point = dernierPointFini(props.graphe.series)
    const sienne = point ? (point.fin ?? point.debut) : null
    if (!sienne || sienne >= onglet) return null
    const date = dateFr(sienne)
    return date ? `dernière valeur le ${date}` : null
})

const phraseSansValeur = computed(() =>
    phraseCarteSansValeur(
        familleDuGraphe(props.graphe.series) ??
            props.graphe.series[0]?.libelleFamille ??
            null,
        dateFr(props.derniereValeurDeLaFamille)
    )
)

/** Le mot de la pastille d'une part : le mot seul, ou le mot et sa part. */
function motDeLaPart(serie: SerieTracee, part: PartOrigine): string {
    return libelleDeLaPart(part, serie.origines?.parts?.length ?? 1)
}

/**
 * LES PARTS D'ORIGINE À ÉCRIRE pour une série. Aucune quand la série n'a pas de
 * mesure sur la fenêtre : ces parts sont lues sur les points du CONTEXTE, et
 * « Mesuré 100 % » à côté de « Aucune mesure sur cette fenêtre » serait deux
 * phrases qui se contredisent sur la même ligne.
 */
function partsDeLaSerie(serie: SerieTracee): PartOrigine[] {
    if (serie.nonPubliee || estVide(serie)) return []
    return serie.origines?.parts ?? []
}

/** `x` peut être une date ou un couple [début, fin] : on prend le début. */
function abscisses(x: Array<string | [string, string]>): string[] {
    return x.map((point) => (Array.isArray(point) ? point[0] : point))
}

/**
 * LA COULEUR EST ATTACHÉE À LA SÉRIE, pas à sa place dans le graphe.
 *
 * L'index de couleur est la position de la clé dans les séries de l'ONGLET,
 * liste stable servie par la carte des onglets. Sans cela, « Puissance active »
 * changeait de couleur dès qu'une série voisine n'avait rien à tracer sur la
 * fenêtre resserrée : la même grandeur, deux couleurs, deux lectures.
 */
function rangDeLaSerie(cle: string): number {
    const exact = props.clesDeLOnglet.indexOf(cle)
    if (exact >= 0) return exact
    // La clé d'une série TRACÉE est celle de l'onglet suivie de sa statistique
    // (« energie-soutirage » devient « energie-soutirage-sum ») : sans ce
    // rattachement, aucune clé ne se retrouvait dans la liste de l'onglet, tout
    // retombait sur le rang 0, et les deux séries d'un même graphe sortaient de
    // la même couleur. On prend le préfixe le PLUS LONG, pour qu'une clé qui en
    // commence une autre ne rafle pas le rang de sa voisine.
    let rang = -1
    let longueur = -1
    props.clesDeLOnglet.forEach((cleOnglet, i) => {
        if (!cle.startsWith(`${cleOnglet}-`)) return
        if (cleOnglet.length <= longueur) return
        longueur = cleOnglet.length
        rang = i
    })
    return rang
}

/**
 * LE VERT DE L'INJECTION (lot 5g). Le vert du kit, celui que la maquette pose
 * sous l'axe : sur une figure en miroir, le sens ne se lit pas seulement à la
 * position, il se lit aussi à la couleur, et une seule teinte pour les deux
 * jeux ferait deux forêts de barres qu'on ne distingue qu'au signe.
 */
const VERT_INJECTION = "#22C55E"

/** Vrai quand cette figure porte les deux sens de part et d'autre de zéro. */
const enMiroir = computed(() => props.graphe.miroir === true)

/** La valeur, écrite comme l'axe l'écrit. Sert au survol du miroir. */
function formaterValeur(valeur: number): string {
    return valeur.toLocaleString("fr-FR", {
        minimumFractionDigits: formatValeur.value === ",.0f" ? 0 : 1,
        maximumFractionDigits: formatValeur.value === ",.0f" ? 0 : 1,
    })
}

/** Vrai quand cette série se dessine SOUS l'axe (miroir seulement). */
function versLeBas(serie: SerieTracee): boolean {
    return enMiroir.value && serie.direction === "injection"
}

function couleurDeLaSerie(cle: string, direction?: DirectionEnedis): string {
    if (enMiroir.value && direction === "injection") return VERT_INJECTION
    const i = rangDeLaSerie(cle)
    return HOMEYS_PLOTLY_COLORWAY[
        (i < 0 ? 0 : i) % HOMEYS_PLOTLY_COLORWAY.length
    ]
}

/**
 * Les motifs de ligne. Ce n'est PAS un encodage de sens : le sens est porté par
 * le nom de la série, dans la légende, dans le survol et dans le tableau. Le
 * motif est une aide de lecture, et il sert deux fois.
 *
 *  • Il écarte deux TEINTES VOISINES au-delà de quatre courbes, là où la
 *    palette commence à se resserrer.
 *  • Il sépare surtout les STATISTIQUES D'UNE MÊME GRANDEUR, qui partagent sa
 *    couleur par construction. Sur l'onglet Puissance d'un C2, trois grandeurs
 *    servies en moyenne, minimum et maximum font SEPT courbes pour trois
 *    teintes : sans motif, la moyenne, le minimum et le maximum d'une même
 *    grandeur sortaient du même trait plein, indiscernables.
 *
 * Le rang du motif additionne le rang de la grandeur et celui de la
 * statistique, dans un ordre FIXE des deux côtés : ni l'un ni l'autre ne dépend
 * de ce que la fenêtre laisse voir, donc un trait ne change pas en resserrant.
 * Cinq motifs pour cinq statistiques : deux séries qui partagent une couleur
 * ont forcément deux rangs de statistique distincts, donc deux traits distincts.
 */
const DASHS = ["solid", "dash", "dot", "dashdot", "longdash"] as const

/** L'ordre de lecture des statistiques, fixe. La moyenne garde le trait plein. */
const ORDRE_STATS: StatSeries[] = ["mean", "sum", "last", "min", "max"]

/**
 * Le motif ne se pose que lorsqu'il sert : au-delà de quatre courbes, ou dès
 * que deux courbes partagent une couleur. En dessous, toutes les lignes sont
 * pleines, comme sur une figure servie.
 */
const motifPose = computed(() => {
    if (tracables.value.length > 4) return true
    const couleurs = tracables.value.map((s) => couleurDeLaSerie(s.cle))
    return new Set(couleurs).size < couleurs.length
})

function dashDeLaSerie(serie: SerieTracee): string {
    if (!motifPose.value) return "solid"
    const grandeur = Math.max(rangDeLaSerie(serie.cle), 0)
    const stat = Math.max(ORDRE_STATS.indexOf(serie.stat), 0)
    return DASHS[(grandeur + stat) % DASHS.length]
}

/**
 * LE FORMAT DES DATES DU SURVOL suit le grain servi : au pas natif et à l'heure,
 * l'heure est écrite ; au jour et au-delà, elle ne veut plus rien dire. Règle
 * reprise de l'API de graphes, pour que la figure se lise comme toutes les
 * autres. L'infobulle a de la place : elle garde la date COMPLÈTE, année
 * comprise, quoi que les graduations décident d'abréger.
 */
const formatSurvolX = computed(() =>
    props.grain === "natif" || props.grain === "hourly"
        ? "%d/%m/%Y à %Hh%M"
        : "%d/%m/%Y"
)

/**
 * LES ANNÉES CIVILES TOUCHÉES PAR CE QU'ON MONTRE. Une seule année autorise à
 * ne pas l'écrire sous chaque graduation ; deux l'exigent, sinon le 01/01 d'une
 * année et celui de la suivante s'écriraient pareil.
 */
const anneesRegardees = computed<number>(() => {
    const annees = new Set<string>()
    if (props.fenetre) {
        annees.add(props.fenetre.debut.slice(0, 4))
        annees.add(props.fenetre.fin.slice(0, 4))
        return annees.size
    }
    for (const serie of tracables.value) {
        for (const x of serie.x) {
            annees.add((Array.isArray(x) ? x[0] : x).slice(0, 4))
        }
    }
    return annees.size || 1
})

/**
 * LE FORMAT DES GRADUATIONS, LISIBLE À LA LARGEUR QU'ELLES ONT (écart G16).
 *
 * LE FAIT, relevé sur les captures du lot 5d1 à 1 366 px : deux figures côte à
 * côte laissent environ 270 px de zone de tracé. `%d/%m/%Y` y demande huit
 * graduations de dix caractères ; Plotly les incline à 30° puis les TRONQUE —
 * « 14/06/202… » sous chaque barre. Une date coupée n'est pas une date.
 *
 * La règle, celle de la maquette : sur une fenêtre qui tient dans UNE année
 * civile, l'année ne se répète pas sous chaque graduation (elle se lit en toutes
 * lettres dans la ligne Fenêtre, juste au-dessus, et dans l'infobulle). Dès que
 * la fenêtre en traverse deux, l'année revient — là, elle distingue.
 *
 * `nticks` borne le nombre de graduations pour que Plotly n'en pose pas plus que
 * la largeur n'en porte, et `tickangle: 0` interdit l'inclinaison : à ce
 * format-là, elles tiennent droites.
 */
const formatGraduationX = computed(() => {
    if (props.grain === "natif" || props.grain === "hourly") {
        return anneesRegardees.value > 1 ? "%d/%m/%y %Hh" : "%d/%m %Hh"
    }
    if (props.grain === "monthly") return "%m/%Y"
    return anneesRegardees.value > 1 ? "%d/%m/%Y" : "%d/%m"
})

/** Combien de graduations la figure porte sans les tronquer, à sa largeur. */
const NTICKS_X = 6

/**
 * LE TITRE DE L'AXE DES TEMPS s'efface au-delà de quatre séries : la légende
 * occupe alors deux lignes sous une zone de tracé de moins de deux cents pixels,
 * et un titre de plus la chevaucherait. En dessous, « Date » est conservé.
 */
const titreAxeX = computed(() => (entreesDeLegende.value >= 5 ? "" : "Date"))

/**
 * DANS LA FENÊTRE REGARDÉE, ou pas. Une seule définition pour les deux lectures
 * qui en dépendent : les bornes de l'axe des ordonnées et les totaux par série.
 * Sans fenêtre, tout le contexte chargé est regardé.
 *
 * Comparaison en chaînes ISO, comme `surRelayout` le fait déjà ; la fin est un
 * JOUR, on la referme donc sur sa dernière seconde, sinon un pas de 13 h du
 * dernier jour tomberait hors de la fenêtre qui le montre.
 */
function dansLaFenetre(x: string): boolean {
    return pointDansLaFenetre(x, props.fenetre)
}

/**
 * LES TOTAUX PAR SÉRIE, SUR LA FENÊTRE REGARDÉE (lot B4).
 *
 * Ce que le lecteur y gagne : la carte répond à « combien, sur ce que je
 * regarde » sans qu'il ait à déplier le tableau et à additionner de tête. Le
 * total suit le recadrage : resserrer la fenêtre recalcule la somme sur les
 * seuls pas montrés, et ne déclenche AUCUN appel — tout se lit dans les points
 * déjà chargés.
 *
 * Deux gardes, les mêmes que celles de la cellule « Total » du tableau :
 *
 *  • un pas absent n'entre pas dans la somme et n'y vaut JAMAIS 0. Il est
 *    COMPTÉ, et le compte s'écrit à côté du nombre : « 1 240 (17 pas
 *    manquants) ». Sans lui, un total plus bas se lirait comme une consommation
 *    plus faible alors que c'est la collecte qui manque (D12, 18ᵉ forme de garde
 *    qui ment). Un `NaN` compte comme une absence, comme partout ici ;
 *  • une série dont AUCUN pas de la fenêtre n'a rendu de valeur n'a pas de
 *    total : elle n'écrit pas « 0 », et pas davantage une parenthèse seule qui
 *    dirait un total de rien. Elle sort du bloc, et la légende de la figure
 *    continue de la nommer.
 */
const totaux = computed<DataChartLegendKpiItem[]>(() => {
    if (!props.sommable) return []
    const items: DataChartLegendKpiItem[] = []
    // LES DURÉES SE LISENT EN HEURES, AVEC UNE SEULE DÉCIMALE (Q25). Les
    // secondes servies par dataV2 sont déjà converties au rangement de la série
    // (écart G11) : ici, seul le FORMAT change — « 2,83 h » disait un centième
    // d'heure, soit trente-six secondes, que rien ne mesure à ce grain.
    const enHeures = estUniteDeDuree(props.graphe.unite)
    const unite = props.graphe.unite ?? undefined
    const ecrit = (valeur: number) =>
        enHeures ? heuresLisibles(valeur) : formateNombre(valeur)

    let totalGeneral: number | null = null
    // LES JOURS SERVIS : ceux qui portent au moins une valeur finie, quelle que
    // soit la classe. C'est sur EUX que le total porte, et le dire évite de
    // lire « 0 h » comme « rien ne s'est passé pendant 92 jours ».
    // UN JOUR NE COMPTE COMME SERVI QUE SI TOUTES LES CLASSES DE LA CARTE Y
    // ONT UNE VALEUR (annexe de la revue du lot 5h) : on compte donc, jour par
    // jour, COMBIEN de classes l'ont servi. Les jours incomplets se disent
    // « partiels » plutôt que de se faire passer pour des jours pleins.
    const classesParJour = new Map<string, Set<string>>()

    // DANS LE BLOC EXHAUSTIF, UNE CLASSE VIDE RESTE NOMMÉE : elle sort de la
    // figure (correctif 5a-2) mais pas de la statistique, sinon le lecteur
    // compterait deux classes là où le compteur en publie trois. Les séries que
    // le compteur NE PUBLIE PAS restent dehors : il n'y a rien à en chiffrer.
    const chiffrees = props.totalDesClasses
        ? props.graphe.series.filter((s) => !s.nonPubliee)
        : tracables.value

    for (const serie of chiffrees) {
        let somme = 0
        let finis = 0
        let regardes = 0
        serie.x.forEach((x, k) => {
            const debut = Array.isArray(x) ? x[0] : x
            if (!dansLaFenetre(debut)) return
            regardes += 1
            const v = serie.v[k]
            if (typeof v !== "number" || !Number.isFinite(v)) return
            somme += v
            finis += 1
            const jour = debut.slice(0, 10)
            const vues = classesParJour.get(jour) ?? new Set<string>()
            vues.add(serie.cle)
            classesParJour.set(jour, vues)
        })
        // UNE CLASSE SANS AUCUNE VALEUR SERVIE N'ÉCRIT PAS 0 (Q25). Dans le
        // bloc exhaustif elle est nommée ; ailleurs elle en sort, comme au
        // lot B4 : une parenthèse seule ne dirait rien.
        if (finis === 0) {
            if (!props.totalDesClasses) continue
            items.push({
                name: serie.libelle,
                color: couleurDeLaSerie(serie.cle, serie.direction),
                value: AUCUNE_VALEUR_SERVIE,
            })
            continue
        }
        totalGeneral = (totalGeneral ?? 0) + somme
        // L'UNITÉ COLLE AU NOMBRE (Q15), Y COMPRIS QUAND DES PAS MANQUENT.
        // Posée dans `unit`, elle tombait APRÈS la parenthèse : « 214 508,64
        // (25 pas manquants) kWh » séparait le chiffre de son unité par une
        // incise. On écrit « 214 508,64 kWh (25 pas manquants) ». Le mot des
        // manquants reste celui de `texteDeLaSomme` : une seule formulation
        // pour toute la page.
        const manquants = regardes - finis
        const brut = ecrit(somme)
        const avecUnite = unite ? `${brut} ${unite}` : brut
        items.push({
            name: serie.libelle,
            color: couleurDeLaSerie(serie.cle, serie.direction),
            value:
                manquants > 0
                    ? texteDeLaSomme(somme, manquants, () => avecUnite)
                    : brut,
            unit: manquants > 0 ? undefined : unite,
        })
    }

    // UN BLOC QUI NE DIRAIT QUE « aucune valeur » NE S'ÉCRIT PAS : la carte le
    // dit déjà une fois sous son titre, et chaque classe le redit sur la
    // figure. Trois fois la même absence n'apprend rien de plus.
    if (props.totalDesClasses && totalGeneral === null) return []

    // LE TOTAL FERME LA LISTE, et ne s'écrit que si UNE valeur au moins est
    // servie : un total de rien n'est pas zéro.
    if (props.totalDesClasses && totalGeneral !== null) {
        const total = totalGeneral
        // LE DÉNOMINATEUR est le nombre de classes qui ont RÉELLEMENT servi
        // quelque chose sur la fenêtre : une classe muette de bout en bout ne
        // rendrait pas tous les jours « partiels », elle dirait seulement
        // qu'elle est muette, et la carte l'écrit déjà à sa ligne.
        const attendues = new Set<string>()
        for (const vues of classesParJour.values()) {
            for (const cle of vues) attendues.add(cle)
        }
        let complets = 0
        let partiels = 0
        for (const vues of classesParJour.values()) {
            if (vues.size === attendues.size) complets += 1
            else partiels += 1
        }
        items.push({
            name: "Total",
            color: "transparent",
            value: texteDuTotalDeLaFenetre(
                ecrit(total),
                unite,
                complets,
                partiels
            ),
        })
    }
    return items
})

/**
 * LES BORNES DE L'AXE DES ORDONNÉES se calculent sur la FENÊTRE REGARDÉE, pas
 * sur tout ce qui est chargé : le chargement déborde d'un quart de chaque côté,
 * et un pic hors fenêtre écrasait la courbe montrée.
 *
 * La règle est celle de l'API de graphes : le bas descend à zéro quand tout est
 * positif, mais un minimum NÉGATIF est conservé (une injection se voit sous
 * l'axe) ; le haut prend 20 % de marge. Sans aucun point fini dans la fenêtre,
 * aucune borne n'est posée : Plotly choisit, plutôt qu'un axe inventé.
 */
const bornesY = computed<[number, number] | null>(() => {
    const finis: number[] = []
    for (const serie of tracables.value) {
        // EN MIROIR, L'AXE DOIT CONTENIR LE BAS AUSSI : les valeurs de
        // l'injection y entrent avec le signe qu'elles auront à l'écran, sans
        // quoi les barres du bas sortiraient du cadre.
        const signe = versLeBas(serie) ? -1 : 1
        serie.x.forEach((x, k) => {
            const v = serie.v[k]
            if (
                typeof v === "number" &&
                Number.isFinite(v) &&
                dansLaFenetre(Array.isArray(x) ? x[0] : x)
            ) {
                finis.push(signe * v)
            }
        })
    }
    // L'AXE BORNÉ AUX VALEURS (écart G9, complément 7.13) et le cadrage à zéro
    // des grandeurs non négatives (lot 5d4) vivent tous deux dans
    // `bornesDeLAxeY` : une seule règle, jamais recopiée carte par carte.
    return bornesDeLAxeY({ finis, auxValeurs: props.axeAuxValeurs })
})

// ============================================================================
// LES TROUS, PEINTS À LEUR DATE (lot B1)
// ============================================================================

/** Le jour ISO suivant. Sert à FERMER une plage : un jour se peint de J à J+1. */
function jourSuivant(jour: string): string {
    const d = new Date(`${jour}T00:00:00Z`)
    if (Number.isNaN(d.getTime())) return jour
    d.setUTCDate(d.getUTCDate() + 1)
    return d.toISOString().slice(0, 10)
}

/** Un jour ISO décalé de `heures`, rendu en horodatage que Plotly sait lire. */
function jourDecaleDe(jour: string, heures: number): string {
    const d = new Date(`${jour}T00:00:00Z`)
    if (Number.isNaN(d.getTime())) return jour
    d.setUTCHours(d.getUTCHours() + heures)
    return d.toISOString().slice(0, 19)
}

/**
 * LES BORNES D'UNE PLAGE, ALIGNÉES SUR CE QUE LA FIGURE DESSINE (écart B1bis).
 *
 * Le fait mesuré dans Chrome, et qui commande cette règle : Plotly range les
 * traces en COUCHES par type, et il dessine `barlayer` AVANT `scatterlayer`.
 * Une bande de fond est un `scatter` rempli : elle passe donc DEVANT les barres,
 * quel que soit son axe et quel que soit l'ordre des traces dans `data`. Ni
 * l'ordre de `data` ni le choix de l'axe ne peuvent y changer quoi que ce soit —
 * seule la GÉOMÉTRIE le peut. (Vérifié le 15/09 :
 * `tests/navigateur/trous-plages-disjointes-enedis.spec.ts`.)
 *
 * Or au grain JOURNALIER, une barre est CENTRÉE sur son jour : celle du jour J
 * court de J−0,5 à J+0,5. Un rectangle de fond posé de J à J+1 recouvrait donc
 * la moitié gauche de la barre du LENDEMAIN — la barre voisine d'un trou
 * sortait rognée, et c'est ce que B1bis demandait de vérifier.
 *
 * La bande couvre donc la CASE du jour absent, comme la bande de complétude :
 * de J−12 h à J+12 h. Aucune barre voisine n'est plus touchée.
 *
 * Aux grains plus fins, un point n'est PAS une journée : il est daté à
 * l'instant. Le jour absent y occupe son intervalle réel, de minuit à minuit, et
 * le décalage d'une demi-journée serait un mensonge d'axe.
 */
function bornesDeLaPlage(plage: string[], grain: string | null): string[] {
    const premier = plage[0]
    const dernier = plage[plage.length - 1]
    if (grain === "daily") {
        return [jourDecaleDe(premier, -12), ...plage, jourDecaleDe(dernier, 12)]
    }
    return [...plage, jourSuivant(dernier)]
}

/**
 * Les jours peints, une fois passés les deux filtres de ce composant.
 *
 * Le grain `periode` est ÉCARTÉ : un point y couvre la période entière, il n'a
 * pas de date de jour à laquelle accrocher un rectangle. Peindre soixante-quatorze
 * jours de hachures à côté d'un unique point de période fabriquerait une queue
 * que la figure ne montre pas — un axe inventé.
 */
const joursPeints = computed<JourAbsent[]>(() => {
    if (props.grain === "periode") return []
    return props.joursAbsents.filter((j) => j?.etat && j.etat !== "mesure")
})

/** Les états réellement peints, dans l'ordre de la légende commune. */
const etatsPeints = computed<EtatCompletude[]>(() => {
    const presents = new Set(joursPeints.value.map((j) => j.etat))
    return ORDRE_LEGENDE.filter((e) => e !== "mesure" && presents.has(e))
})

/** Les plages de jours CONTIGUS d'une liste triée, sans doublon. */
function plagesContigues(joursTries: string[]): string[][] {
    const plages: string[][] = []
    for (const jour of joursTries) {
        const derniere = plages[plages.length - 1]
        const precedent = derniere?.[derniere.length - 1]
        if (
            derniere &&
            precedent !== undefined &&
            jourSuivant(precedent) === jour
        ) {
            derniere.push(jour)
        } else {
            plages.push([jour])
        }
    }
    return plages
}

/**
 * UNE TRACE DE FOND PAR PLAGE CONTIGUË, sur un axe superposé et invisible.
 *
 * La forme : une trace par plage de jours contigus d'un même état, dont le `x`
 * est la plage ELLE-MÊME et rien d'autre — un point par jour absent (pour que le
 * survol trouve un point à la date qu'on montre), plus le lendemain du dernier
 * jour pour FERMER le rectangle. Aucun `null` : il n'y a plus jamais deux plages
 * dans la même trace.
 *
 * POURQUOI (défaut vu en PROD le 15/09, C5 réel, 314 jours dont 2 absents aux
 * deux bouts) : la forme précédente mettait toutes les plages d'un état dans UNE
 * trace et comptait sur un `null` entre elles, avec `connectgaps: false`, pour
 * couper le remplissage. `connectgaps` gouverne la LIGNE, pas la surface :
 * Plotly a rempli tout l'intervalle entre le premier et le dernier point, le
 * graphe entier est sorti hachuré et les 312 barres mesurées ont disparu
 * dessous. Un `null` ne sépare pas deux surfaces ; seule une trace séparée le
 * fait. Corollaire : ne jamais faire porter à un `null` la séparation de deux
 * plages remplies.
 *
 * `legendgroup` commun par état : les plages d'un même état restent une seule
 * chose pour Plotly (survol, sélection), même écrites en plusieurs traces.
 *
 * `showlegend: false` n'est pas cosmétique : `PlotlyChart` ne compte, pour ses
 * bornes d'axe, que les traces visibles ET légendées. Une bande légendée
 * entrerait dans ce calcul et écraserait l'échelle des séries.
 *
 * POURQUOI `y3` ET NON `y2` : le même calcul n'écrase que `yaxis` et `yaxis2`.
 * Une bande posée sur `y2` s'effondrerait au premier clic dans la légende.
 */
const bandesDeFond = computed(() => {
    const parEtat = new Map<EtatCompletude, Set<string>>()
    for (const { jour, etat } of joursPeints.value) {
        if (!parEtat.has(etat)) parEtat.set(etat, new Set())
        parEtat.get(etat)?.add(jour)
    }
    const bandes: Record<string, unknown>[] = []
    for (const etat of etatsPeints.value) {
        const descripteur = ETATS_COMPLETUDE[etat]
        const tries = [...(parEtat.get(etat) ?? [])].sort()
        for (const plage of plagesContigues(tries)) {
            // Les bornes de CETTE plage seulement : les jours peints, plus ce
            // qu'il faut pour fermer le rectangle sur la CASE de ces jours
            // (`bornesDeLaPlage`, écart B1bis).
            const x = bornesDeLaPlage(plage, props.grain)
            bandes.push({
                type: "scatter",
                mode: "none",
                yaxis: "y3",
                name: descripteur.mot,
                legendgroup: `trous-${etat}`,
                x,
                y: x.map(() => 1),
                fill: "tozeroy",
                fillcolor: descripteur.fondHex,
                // Les hachures de la bande de complétude, à l'identique : même
                // teinte, même inclinaison. Le motif porte l'absence tout seul,
                // sans dépendre de la couleur (noir et blanc, daltonisme).
                fillpattern: {
                    shape: "/",
                    fgcolor: descripteur.traitHex,
                    bgcolor: descripteur.fondHex,
                    size: 4,
                    solidity: 0.35,
                },
                line: { width: 0 },
                connectgaps: false,
                // LE MOT DE LA BANDE, et rien d'autre : pas de valeur, surtout
                // pas un 0. Un jour sans donnée n'a pas de valeur à montrer, il
                // a un état à nommer.
                hovertemplate: `${descripteur.mot}<extra></extra>`,
                showlegend: false,
            })
        }
    }
    return bandes
})

/** Le nom du fichier de l'image exportée (marque blanche : aucun mot interne). */
const nomDuFichier = computed(() =>
    nomDuFichierDeGraphe(props.prm, props.cleOngletLisible)
)

/**
 * LA FIGURE. Elle passe par le pipeline du kit (polices, axes, grille, survol
 * `x unified`), puis reçoit les seules clés que le kit ne pose pas et que cette
 * page décide : la palette, les suffixes de graduation, les marges du kit, le
 * recul du titre d'ordonnée.
 *
 * Deux précautions, apprises en les lisant dans le pipeline :
 *  • le troisième argument (`dateXAxisFr`) n'est JAMAIS passé : il écraserait
 *    `tickformat` et `hoverformat` après la fusion, et l'heure disparaîtrait du
 *    pas natif ;
 *  • les marges et le recul du titre se posent APRÈS l'appel, parce que le
 *    pipeline écrase `margin` sans condition et pose un recul de 10.
 */
/**
 * LES ENVELOPPES DE CE GRAPHE, sur les seules séries traçables.
 *
 * Une enveloppe dont le minimum ou le maximum est vide sur la fenêtre regardée
 * n'en est plus une : la série vide est déjà sortie de `tracables`, et le groupe
 * retombe donc tout seul en courbes. C'est voulu — on ne peint pas une aire dont
 * une borne manque.
 */
const enveloppes = computed<GroupeEnveloppe[]>(() =>
    groupesDEnveloppe(tracables.value).filter((g) => g.min && g.max)
)

/** Les clés des séries absorbées par une enveloppe : elles ne sont plus seules. */
const clesEnveloppees = computed<Set<string>>(() => {
    const cles = new Set<string>()
    for (const groupe of enveloppes.value) {
        cles.add(groupe.mean.cle)
        if (groupe.min) cles.add(groupe.min.cle)
        if (groupe.max) cles.add(groupe.max.cle)
    }
    return cles
})

/**
 * COMBIEN D'ENTRÉES LA LÉGENDE PORTERA (écart G12, décision Q18).
 *
 * Une série unique n'a pas besoin de légende : le titre la nomme déjà, et la
 * bande de légende mangeait trente pixels de zone de tracé pour répéter le
 * titre. Une enveloppe compte pour UNE entrée, pas trois.
 */
const entreesDeLegende = computed(
    () =>
        enveloppes.value.length +
        tracables.value.filter((s) => !clesEnveloppees.value.has(s.cle)).length
)

/** La valeur d'un point de série à une date donnée, ou `null`. */
function valeurALaDate(
    serie: SerieTracee | null,
    index: Map<string, number>,
    date: string
): number | null {
    if (!serie) return null
    const k = index.get(date)
    if (k === undefined) return null
    const v = serie.v[k]
    return typeof v === "number" && Number.isFinite(v) ? v : null
}

/**
 * Un nombre tel qu'il s'écrit dans une infobulle, avec le formateur de la page.
 * Une borne non servie n'écrit PAS 0 : elle écrit qu'elle n'est pas là.
 */
function nombreLisible(v: number | null): string {
    return v === null ? "non servi" : formateNombre(v)
}

/** Une heure servie, ramenée aux heures et minutes : « 18:08:13 » → « 18:08 ». */
function heureLisible(at: string | null | undefined): string | null {
    if (typeof at !== "string" || at.length < 4) return null
    const trouve = /(\d{2}):(\d{2})/.exec(at)
    return trouve ? `${trouve[1]}h${trouve[2]}` : null
}

/** Le mot client d'une nature de période. Jamais le mot servi, qui est interne. */
const LIBELLES_NATURE_PERIODE: Record<string, string> = {
    REEL: "Relevé réel",
    ESTIME: "Relevé estimé",
    REGU: "Régularisation",
}

/**
 * CE QUI S'ÉCRIT SOUS LA VALEUR, DANS L'INFOBULLE D'UN POINT.
 *
 * Trois faits, chacun seulement quand le lecteur l'a SERVI, et tous alignés sur
 * `x` : l'origine du point, l'heure à laquelle une PMax a été atteinte (la seule
 * chose que la figure ne peut pas montrer : un point posé sur un jour ne dit pas
 * à quelle heure de ce jour la pointe est tombée), et la nature d'une période de
 * facturation. Une nature que le lecteur ne dit pas ne s'écrit pas « Réel » :
 * elle ne s'écrit pas du tout.
 */
function lignesDeSurvol(serie: SerieTracee): string[] {
    return serie.origines.parPoint.map((o, k) => {
        const lignes: string[] = []
        if (o) lignes.push(LIBELLES_ORIGINE[o])
        const heure = heureLisible(serie.at?.[k])
        if (heure) lignes.push(`Atteinte à ${heure}`)
        const nature = serie.natureParPoint?.[k]
        const motNature = nature ? LIBELLES_NATURE_PERIODE[nature] : null
        if (motNature) lignes.push(motNature)
        const x = serie.x[k]
        // L'EMPRISE SE DIT AU SURVOL : « du 01/08/2026 au 31/08/2026 » (Q24).
        // « au » est le DERNIER JOUR COUVERT — la fin servie est exclusive
        // (lot 5d4c) — et le mois lit sa longueur du calendrier. Le survol
        // disait la même chose que le tableau, et tous deux faisaient partager
        // un jour à deux périodes.
        const fin = finCouverteDuPoint(x, props.grain)
        if (fin) {
            const debut = Array.isArray(x) ? x[0] : x
            lignes.push(`du ${dateFr(debut) ?? debut} au ${dateFr(fin) ?? fin}`)
        } else if (Array.isArray(x)) {
            lignes.push(`du ${dateFr(x[0]) ?? x[0]} au ${dateFr(x[1]) ?? x[1]}`)
        }
        return lignes.join("<br>")
    })
}

/**
 * LA LARGEUR ET LA POSITION D'UNE BARRE (écart G10, décisions Q14 et Q24).
 *
 * CHAQUE BARRE COUVRE SA PÉRIODE, du premier jour au dernier, quel que soit le
 * grain (Q24 du 17/09). Sans largeur explicite, Plotly centre la barre sur son
 * abscisse et prend le plus petit écart de la série : un mois d'août se lisait
 * à cheval sur la fin juillet, et tous les mois portaient la largeur du plus
 * court. L'emprise se lit de la période servie quand elle l'est, sinon du
 * calendrier du grain (`empriseDuPoint`).
 *
 * `width` et `offset` sont donnés POINT PAR POINT, parce que les périodes n'ont
 * pas toutes la même durée (le C5 réel : sept jours, puis quatorze, puis
 * trente et un ; et un mois fait 28, 30 ou 31 jours). Chaque classe prend UNE
 * part de la largeur, dans l'ordre stable des séries de l'onglet : côte à côte,
 * et non empilées (Q14).
 *
 * `offset` compte depuis `x`, qui est le PREMIER jour de la période : la classe
 * de rang k occupe `[début + k·part, début + (k+1)·part]`.
 *
 * AU GRAIN `natif`, le pas dépend du compteur et n'est pas connu ici : aucune
 * emprise n'est posée plutôt qu'une largeur inventée, et Plotly décide.
 *
 * L'OPACITÉ dit la NATURE : un relevé estimé se peint à moitié transparent, un
 * relevé réel plein. Une nature que le lecteur ne sert pas laisse la barre
 * PLEINE — atténuer par défaut ferait passer tout un historique pour estimé.
 */
function rectanglesDeLaPeriode(serie: SerieTracee): Record<string, unknown> {
    if (!empriseConnue.value) return {}
    const rang = Math.max(tracables.value.indexOf(serie), 0)
    const parts = Math.max(tracables.value.length, 1)
    // LES BARRES SONT BORNÉES AU CONTEXTE CHARGÉ (annexe de la revue du 5h) :
    // un mois tronqué en bord de contexte occupe SA part visible, et son bord
    // gauche se décale d'autant. Sans contexte, rien ne change.
    const bornes = serie.x.map((x) => {
        const xMs = Date.parse(
            `${String(Array.isArray(x) ? x[0] : x).slice(0, 10)}T00:00:00Z`
        )
        const borne = bornesDuPoint(x, props.grain, props.contexte)
        if (!borne || !Number.isFinite(xMs)) {
            return { duree: JOUR_MS, decalage: 0 }
        }
        return { duree: borne.fin - borne.debut, decalage: borne.debut - xMs }
    })
    return {
        width: bornes.map((b) => b.duree / parts),
        offset: bornes.map((b) => b.decalage + (rang * b.duree) / parts),
        marker: {
            cornerradius: "1%",
            // LE SENS DÉCIDE DE LA COULEUR EN MIROIR (lot 5g) : ce `marker`
            // écrase celui de la trace, il doit donc porter la même règle.
            color: couleurDeLaSerie(serie.cle, serie.direction),
            opacity: serie.x.map((_, k) =>
                serie.natureParPoint?.[k] === "ESTIME" ? 0.5 : 1
            ),
        },
    }
}

/**
 * CE GRAIN DONNE-T-IL UNE EMPRISE ? Vrai dès que la durée d'un point se lit —
 * de la période servie ou du calendrier du grain. Faux au grain `natif`, dont
 * le pas appartient au compteur.
 */
const empriseConnue = computed(
    () =>
        props.rendu === "barres" &&
        props.graphe.series.some(
            (s) =>
                s.x.length > 0 && empriseDuPoint(s.x[0], props.grain) !== null
        )
)

/**
 * LA FENÊTRE NE CONTIENT AUCUNE PÉRIODE CLOSE (complément 7.12).
 *
 * Distinct d'une grandeur non publiée ET d'une fenêtre sans mesure : le compteur
 * facture, il a des périodes, et c'est celle qu'on regarde qui n'en a pas encore
 * fermé une. Le lecteur le dit d'une façon qui ne trompe pas — il rend la série
 * avec `x` VIDE, et non des zéros (vérifié le 15/09 sur le C2, du 05 au 10/09 :
 * `npts` 0).
 */
const aucunePeriodeClose = computed(
    () =>
        props.grain === "periode" &&
        props.graphe.series.length > 0 &&
        tracables.value.length === 0
)

const graph = computed(() => {
    const unite = props.graphe.unite ?? ""

    // L'AIRE ENTRE LE MINIMUM ET LE MAXIMUM (écart G5). Trois traces, dans CET
    // ordre : le maximum (invisible), le minimum rempli `tonexty` — donc jusqu'à
    // la trace précédente, c'est-à-dire le maximum — puis la moyenne, seule
    // nommée. `tonexty` remplit vers la trace qui PRÉCÈDE dans `data` : l'ordre
    // n'est pas cosmétique, il EST le remplissage.
    const tracesEnveloppe: Record<string, unknown>[] = []
    for (const groupe of enveloppes.value) {
        const couleur = couleurDeLaSerie(groupe.mean.cle, groupe.mean.direction)
        // EN MIROIR, L'ENVELOPPE D'INJECTION SE RENVERSE ELLE AUSSI, aire
        // comprise : une enveloppe posée en haut sous un titre qui annonce le
        // bas serait la contradiction la plus lisible de la figure.
        const bas = versLeBas(groupe.mean)
        const signer = (
            valeurs: Array<number | null | undefined>
        ): Array<number | null | undefined> =>
            bas
                ? valeurs.map((v) =>
                      typeof v === "number" && Number.isFinite(v) ? -v : v
                  )
                : valeurs
        const x = abscisses(groupe.mean.x)
        const indexMin = new Map(
            abscisses(groupe.min?.x ?? []).map((d, k) => [d, k])
        )
        const indexMax = new Map(
            abscisses(groupe.max?.x ?? []).map((d, k) => [d, k])
        )
        const bornes = x.map((date) => ({
            min: valeurALaDate(groupe.min, indexMin, date),
            max: valeurALaDate(groupe.max, indexMax, date),
        }))
        const muet = {
            type: "scatter",
            mode: "lines",
            line: { width: 0, color: couleur },
            showlegend: false,
            hoverinfo: "skip",
            connectgaps: false,
            legendgroup: groupe.cle,
        }
        tracesEnveloppe.push(
            {
                ...muet,
                name: `${groupe.libelleBase} (maximum)`,
                x,
                y: signer(bornes.map((b) => b.max)),
            },
            {
                ...muet,
                name: `${groupe.libelleBase} (minimum)`,
                x,
                y: signer(bornes.map((b) => b.min)),
                fill: "tonexty",
                // 22 % : l'aire se voit sans effacer la moyenne qui la traverse,
                // ni les hachures d'absence qu'elle recouvre.
                fillcolor: `${couleur}38`,
            },
            {
                type: "scatter",
                mode: "lines",
                name: enMiroir.value
                    ? `${libelleDeLEnveloppe(groupe)} · ${LIBELLES_DIRECTION[groupe.mean.direction]}`
                    : libelleDeLEnveloppe(groupe),
                meta: [
                    enMiroir.value
                        ? `${libelleDeLEnveloppe(groupe)} · ${LIBELLES_DIRECTION[groupe.mean.direction]}`
                        : libelleDeLEnveloppe(groupe),
                ],
                legendgroup: groupe.cle,
                x,
                y: signer(groupe.mean.v),
                line: { width: 1, color: couleur },
                connectgaps: false,
                // Les trois valeurs dans UNE infobulle : sans elles, le minimum
                // et le maximum seraient tracés et illisibles au survol, puisque
                // leurs traces se taisent.
                customdata: groupe.mean.origines.parPoint.map((o, k) => {
                    const mot = o ? LIBELLES_ORIGINE[o] : ""
                    const b = bornes[k]
                    const intervalle = `min ${nombreLisible(b?.min ?? null)} · max ${nombreLisible(b?.max ?? null)}`
                    return mot ? `${mot}<br>${intervalle}` : intervalle
                }),
                // Le survol LIT la moyenne en valeur absolue, comme les
                // barres : le dessin est renversé, la valeur ne l'est pas.
                text: bas
                    ? groupe.mean.v.map((v) =>
                          typeof v === "number" && Number.isFinite(v)
                              ? formaterValeur(Math.abs(v))
                              : ""
                      )
                    : undefined,
                hovertemplate: bas
                    ? `<b>%{text}</b>${unite ? ` ${unite}` : ""}<extra>%{meta[0]}</extra><br>%{customdata}`
                    : buildTooltipChartTemplate({
                          yFormat: formatValeur.value,
                          unit: unite,
                          hasCustomData: true,
                      }),
            }
        )
    }

    const traces = tracables.value
        .filter((s) => !clesEnveloppees.value.has(s.cle))
        .map((serie) => {
            // L'origine du point, alignée sur `x`. Un pas absent porte `null` en
            // valeur : il ne paraît pas dans le survol, donc aucun mot d'origine ne
            // s'écrit jamais sur un trou.
            const customdata = lignesDeSurvol(serie)
            // LE MIROIR DESSINE L'INJECTION VERS LE BAS, et la LIT en valeur
            // absolue (lot 5g) : « −12,4 kWh injectés » serait un nombre faux.
            // Seul le DESSIN est renversé ; la valeur servie, elle, ne bouge
            // pas, et rien n'est jamais soustrait (E5).
            const bas = versLeBas(serie)
            const hovertemplate = bas
                ? `<b>%{text}</b>${unite ? ` ${unite}` : ""}<extra>%{meta[0]}</extra><br>%{customdata}`
                : buildTooltipChartTemplate({
                      yFormat: formatValeur.value,
                      unit: unite,
                      hasCustomData: true,
                  })
            const commun = {
                name: bas
                    ? `${serie.libelle} · ${LIBELLES_DIRECTION.injection}`
                    : enMiroir.value
                      ? `${serie.libelle} · ${LIBELLES_DIRECTION[serie.direction]}`
                      : serie.libelle,
                meta: [
                    bas || enMiroir.value
                        ? `${serie.libelle} · ${LIBELLES_DIRECTION[serie.direction]}`
                        : serie.libelle,
                ],
                x: abscisses(serie.x),
                y: bas
                    ? serie.v.map((v) =>
                          typeof v === "number" && Number.isFinite(v) ? -v : v
                      )
                    : serie.v,
                ...(bas
                    ? {
                          text: serie.v.map((v) =>
                              typeof v === "number" && Number.isFinite(v)
                                  ? formaterValeur(Math.abs(v))
                                  : ""
                          ),
                      }
                    : {}),
                customdata,
                hovertemplate,
            }
            // LA PMAX RELEVÉE SE PEINT EN POINTS, pas en courbe et pas en barre.
            // Une valeur par jour relevée par le compteur n'est pas un signal
            // continu : la relier tracerait un trajet entre deux pointes qui
            // n'a jamais existé, et la mettre en barre la ferait lire comme une
            // quantité consommée.
            if (serie.pmaxMesuree) {
                return {
                    ...commun,
                    type: "scatter",
                    mode: "markers",
                    marker: {
                        size: 5,
                        color: couleurDeLaSerie(serie.cle, serie.direction),
                        line: { width: 0 },
                    },
                }
            }
            if (props.rendu === "barres") {
                return {
                    ...commun,
                    type: "bar",
                    marker: {
                        cornerradius: "1%",
                        color: couleurDeLaSerie(serie.cle, serie.direction),
                    },
                    // LES RECTANGLES D'UNE PÉRIODE DE FACTURATION (écart G10).
                    // Au grain `periode` une barre couvre SA durée, du premier
                    // au dernier jour, et les classes se rangent côte à côte
                    // dans cette largeur (Q14). Posée au début sans largeur,
                    // elle se lisait comme un relevé d'un jour.
                    ...rectanglesDeLaPeriode(serie),
                    opacity: 1,
                }
            }
            return {
                ...commun,
                type: "scatter",
                mode: "lines",
                line: {
                    width: 1,
                    dash: dashDeLaSerie(serie),
                    color: couleurDeLaSerie(serie.cle, serie.direction),
                },
                marker: { size: 5, opacity: 0.5, line: { width: 0 } },
                // Un pas non collecté fait un BLANC. Il ne se relie pas, il ne vaut pas 0.
                connectgaps: false,
            }
        })

    // LA PMAX RELEVÉE POSÉE SUR LA COURBE DU JOUR (complément 7.11). Des points,
    // sur un axe À ELLE, à droite : des kVA relevés et des kW dérivés ne
    // partagent pas une échelle, et les superposer sur `yaxis` ferait lire un
    // dépassement inexistant. Le titre nomme les deux grandeurs et leurs unités.
    const pmaxPosee = props.graphe.pmaxPosee
    const tracesPmaxPosee: Record<string, unknown>[] =
        pmaxPosee && !estVide(pmaxPosee)
            ? [
                  {
                      type: "scatter",
                      mode: "markers",
                      name: `${pmaxPosee.libelle} (${pmaxPosee.unite ?? ""})`.trim(),
                      meta: [pmaxPosee.libelle],
                      x: abscisses(pmaxPosee.x),
                      y: pmaxPosee.v,
                      yaxis: "y2",
                      marker: {
                          size: 5,
                          color: couleurDeLaSerie(pmaxPosee.cle),
                          line: { width: 0 },
                      },
                      customdata: lignesDeSurvol(pmaxPosee),
                      hovertemplate: buildTooltipChartTemplate({
                          yFormat: ".1f",
                          unit: pmaxPosee.unite ?? "",
                          hasCustomData: true,
                      }),
                  },
              ]
            : []

    const partiel: Record<string, unknown> = {
        // Le cliquer-glisser sur l'axe des temps EST le zoom de cette page.
        dragmode: "zoom",
        height: props.hauteur,
        colorway: [...HOMEYS_PLOTLY_COLORWAY],
        // La légende dès DEUX choses à distinguer, jamais pour une seule que le
        // titre nomme déjà (écart G12, décision Q18).
        showlegend: entreesDeLegende.value > 1,
        // DÈS QUE L'EMPRISE EST CONNUE, LES LARGEURS SONT POSÉES À LA MAIN
        // (G10, Q24) : chaque classe occupe sa part de la durée de la période.
        // `group` recalculerait ces largeurs et ces décalages par-dessus les
        // nôtres, et les rectangles ne couvriraient plus la période. `overlay`
        // laisse `width` et `offset` faire exactement ce qu'ils disent.
        barmode: empriseConnue.value ? "overlay" : "group",
        legend: { groupclick: "toggleitem", valign: "middle" },
        modebar: {
            color: "rgba(68, 68, 68, 0.3)",
            activecolor: "rgba(68, 68, 68, 0.7)",
            bgcolor: "#FFFFFF",
            orientation: "v",
        },
        xaxis: {
            type: "date",
            fixedrange: false,
            tickformat: formatGraduationX.value,
            hoverformat: formatSurvolX.value,
            tickangle: 0,
            nticks: NTICKS_X,
            automargin: true,
            ticksuffix: "  ",
            // Les bornes de la fenêtre regardée, telles quelles : ce qui est
            // chargé déborde d'un quart de chaque côté, ce qui est MONTRÉ non.
            ...(props.fenetre
                ? {
                      range: [props.fenetre.debut, props.fenetre.fin],
                      autorange: false,
                  }
                : {}),
        },
        yaxis: {
            fixedrange: true,
            // LE ZÉRO EST LA LIGNE DE PARTAGE DU MIROIR : il se voit, tracé,
            // sinon rien ne sépare le haut du bas.
            ...(enMiroir.value ? { zeroline: true, zerolinewidth: 1.2 } : {}),
            // `tozero` fait exactement ce qu'il dit : il RAMÈNE l'axe à zéro,
            // même quand on lui pose des bornes. Sur un index, il écrasait la
            // courbe sur un millième de la hauteur de la figure. En miroir, il
            // écraserait la moitié basse de la figure : l'axe doit porter les
            // deux signes.
            ...(props.axeAuxValeurs || enMiroir.value
                ? {}
                : { rangemode: "tozero" as const }),
            tickformat: formatValeur.value,
            ticksuffix: "  ",
            ...(bornesY.value
                ? { range: bornesY.value, autorange: false }
                : {}),
        },
        // L'AXE DE LA PMAX POSÉE : superposé, à droite, gradué dans SON unité.
        // Il n'est là que quand des points y sont posés ; sans lui, les kVA
        // relevés se seraient lus sur l'échelle des kW dérivés.
        ...(tracesPmaxPosee.length
            ? {
                  yaxis2: {
                      overlaying: "y",
                      side: "right",
                      fixedrange: true,
                      rangemode: "tozero",
                      showgrid: false,
                      title: { text: pmaxPosee?.unite ?? "", standoff: 10 },
                  },
              }
            : {}),
        // LES DEUX MOTS DU MIROIR, posés dans la figure (lot 5g) : sans eux,
        // un lecteur qui arrive sur la carte doit deviner ce que veut dire une
        // barre sous l'axe. Ce ne sont pas des bannières : ce sont les
        // étiquettes de deux moitiés d'axe, comme sur la maquette.
        ...(enMiroir.value
            ? {
                  annotations: [
                      {
                          text: `${LIBELLES_DIRECTION.soutirage.toLowerCase()} ↑`,
                          xref: "paper",
                          yref: "paper",
                          x: 1,
                          y: 1,
                          xanchor: "right",
                          yanchor: "top",
                          showarrow: false,
                          font: { size: 11 },
                      },
                      {
                          text: `${LIBELLES_DIRECTION.injection.toLowerCase()} ↓`,
                          xref: "paper",
                          yref: "paper",
                          x: 1,
                          y: 0,
                          xanchor: "right",
                          yanchor: "bottom",
                          showarrow: false,
                          font: { size: 11, color: VERT_INJECTION },
                      },
                  ],
              }
            : {}),
        // L'axe des bandes : superposé, borné à [0, 1], invisible et figé. Il
        // n'a ni graduation ni titre ; il ne sert qu'à donner aux rectangles une
        // hauteur qui remplit la zone de tracé quoi qu'il arrive à l'échelle des
        // séries. N'est posé que s'il y a quelque chose à peindre.
        ...(bandesDeFond.value.length
            ? {
                  yaxis3: {
                      overlaying: "y",
                      range: [0, 1],
                      visible: false,
                      fixedrange: true,
                      showgrid: false,
                  },
              }
            : {}),
    }

    const figure = finalizeHomeysPlotlyGraph(
        // Les bandes AVANT les séries : elles sont un fond, jamais un tracé, et
        // l'ordre des traces est l'ordre de dessin.
        {
            // La PMax posée passe EN DERNIER : ses points se lisent par-dessus
            // l'aire de l'enveloppe, jamais dessous.
            data: [
                ...bandesDeFond.value,
                ...tracesEnveloppe,
                ...traces,
                ...tracesPmaxPosee,
            ],
            layout: partiel,
        },
        { name: "donnees_compteur", x_label: titreAxeX.value, y_label: unite }
    )

    const layout = figure.layout as Record<string, unknown>
    layout.margin = { l: 80, r: 40, t: 20, b: 40 }
    const yaxis = layout.yaxis as Record<string, unknown> | undefined
    const titreY = yaxis?.title as Record<string, unknown> | undefined
    if (titreY) titreY.standoff = 25
    // LE ZÉRO EST LA LIGNE DE PARTAGE DU MIROIR : il se voit, tracé, sinon rien
    // ne sépare le haut du bas. Posé APRÈS le pipeline du kit, qui éteint la
    // ligne de zéro de toutes les figures — et il a raison pour toutes les
    // autres, où elle se confondrait avec l'axe.
    if (enMiroir.value && yaxis) {
        yaxis.zeroline = true
        yaxis.zerolinewidth = 1.2
        yaxis.zerolinecolor = "#ACAEB2"
    }

    return figure
})

/**
 * Le glisser de Plotly arrive en `relayout`. Deux formes : les bornes explicites
 * (`xaxis.range[0]`), ou l'autorange après un double-clic, qui n'est PAS un
 * recadrage et qu'on laisse passer.
 */
/**
 * LE TITRE DE LA CARTE, AVEC CE QUE LA FIGURE MONTRE VRAIMENT (écart G3).
 *
 * « absences hachurées » de la maquette ne s'écrit QUE si des absences sont
 * réellement peintes sur cette figure. L'écrire sur un compteur complet
 * annoncerait des hachures qu'on ne verrait nulle part, et laisserait chercher.
 */
const titreDeLaCarte = computed(() =>
    // `figureRemise` reste la condition qui compte : sur une lecture qui a
    // échoué, aucune figure n'est remise, et annoncer des hachures sous un
    // titre puis n'en montrer aucune ferait chercher le lecteur.
    bandesDeFond.value.length && figureRemise.value
        ? `${props.graphe.titre}, absences hachurées`
        : props.graphe.titre
)

function surRelayout(evenement: Record<string, unknown> | undefined): void {
    if (!evenement) return
    // Un AUTORANGE n'est pas un geste de recadrage : c'est Plotly qui recalcule
    // son axe (autoscale, retour au double-clic, redessin après un nouveau jeu
    // de points). Le traiter comme un glisser faisait recharger la fenêtre à
    // chaque redessin, qui redessinait, qui rechargeait : le fil principal ne
    // rendait plus la main (staging, 10/09).
    if ("xaxis.autorange" in evenement) return
    const debut = evenement["xaxis.range[0]"]
    const fin = evenement["xaxis.range[1]"]
    if (typeof debut !== "string" || typeof fin !== "string") return
    // Le range que NOUS venons de poser nous revient à chaque redessin. Le
    // prendre pour un glisser, c'était recharger ce qu'on regardait déjà : le
    // « Revenir au contexte » du 10/09 partait ainsi en un appel de trop.
    if (
        props.fenetre &&
        jourDe(debut) === props.fenetre.debut &&
        jourDe(fin) === props.fenetre.fin
    ) {
        return
    }
    emit("brush", { debut, fin })
}
</script>

<!-- LA CARTE DE GRAPHE DE L'APPLICATION, celle du tableau de bord et de la page
     GRDF : même cadre, même entête, même pied. `data-graphe` tombe sur la racine
     de la carte par transmission d'attribut (composant à racine unique), et les
     crochets `[data-graphe]` et `[data-graphe] [data-origine]` des specs restent
     trouvables. Ce commentaire vit HORS du gabarit : posé avant la racine, il en
     ferait un composant à racines multiples, et la transmission d'attribut
     cesserait. -->
<template>
    <PlotlyChartCard
        :data-graphe="graphe.cle"
        :card-id="`graphe-${graphe.cle}`"
        :title="titreDeLaCarte"
        title-class="graphe-enedis__tete text-base!"
        :graph="figureRemise ? graph : null"
        :loading="chargement && tracables.length === 0"
        :content-min-height="figureRemise ? hauteurMinimale : '6rem'"
        :preserve-layout-range="true"
        :mode-bar-buttons-to-remove-extra="['autoScale2d']"
        :to-image-filename="nomDuFichier"
        @relayout="surRelayout"
        @doubleclick="emit('retour')"
    >
        <!-- L'origine du graphe entier, quand elle est une : un mot, une fois,
             à côté du titre — hors du `h2`, pour ne pas polluer le texte du
             titre lu par une aide technique. -->
        <template #title-extra>
            <DataOrigineBadge
                v-if="origineDuGraphe"
                :origine="origineDuGraphe"
                :libelle="LIBELLES_ORIGINE[origineDuGraphe]"
            />

            <!-- DE QUOI C'EST CALCULÉ (R4). La pastille dit « Calculé » ; sans
                 ce complément, elle ne dit pas à partir de quoi, et l'enveloppe
                 pouvait passer pour un modèle. -->
            <span
                v-if="complementDeLOrigine"
                class="text-color text-xs"
                data-testid="origine-du-calcul"
                >{{ complementDeLOrigine }}</span
            >

            <!-- LE PAS DE CETTE CARTE (R1/R7). Un groupe de boutons, pas un
                 menu : les trois pas tiennent en trois mots, et le lecteur doit
                 voir d'un coup d'oeil lequel est servi ET ce qui existe à
                 côté. `aria-pressed` porte l'état, comme partout. -->
            <span
                v-if="pasAOffrir.length"
                class="inline-flex items-center gap-1"
                role="group"
                aria-label="Pas de temps de la carte"
                data-testid="pas-de-la-carte"
            >
                <button
                    v-for="pas in pasAOffrir"
                    :key="pas"
                    type="button"
                    class="border-surface-300 text-color cursor-pointer rounded-full border border-solid bg-transparent px-2 py-0.5 text-xs"
                    :class="
                        pas === pasServi
                            ? 'border-primary text-primary font-medium'
                            : ''
                    "
                    :data-pas="pas"
                    :aria-pressed="pas === pasServi ? 'true' : 'false'"
                    @click="choisirLePas(pas)"
                >
                    {{ libelleDuPas(pas) }}
                </button>
            </span>

            <!-- POURQUOI CE N'EST PAS LE PAS DEMANDÉ. Dans la carte, à côté de
                 son sélecteur : un trou ou un refus se représente là où il
                 s'applique, pas en haut de page. -->
            <span
                v-if="raisonDuPas"
                class="text-color text-xs"
                data-testid="raison-du-pas"
                >{{ raisonDuPas }}</span
            >

            <!-- LE NIVEAU, DIT LÀ OÙ IL CHANGE CE QU'ON VOIT (écart T4, Q8).
                 Une puce, pas une phrase : la règle complète se lit dans la
                 barre de contexte, à côté de la largeur qui la décide. -->
            <span
                v-if="niveauLisible"
                class="border-surface-300 text-color inline-flex items-center rounded-full border border-solid px-2 py-0.5 text-xs"
                data-testid="niveau-de-la-carte"
                >Niveau : {{ niveauLisible }}</span
            >

            <!-- LA DATE DE CETTE CARTE, quand sa famille est en retard sur
                 celle de l'onglet (lot 5d4b). Une date, sans verdict : un
                 trait qui s'arrête sans un mot laisse chercher. -->
            <span
                v-if="fraicheurDeLaCarte"
                class="text-color text-xs"
                data-testid="fraicheur-de-la-carte"
                >{{ fraicheurDeLaCarte }}</span
            >

            <!-- LA FAMILLE EST FIGÉE AVANT LA FENÊTRE (lot 5d4, déplacée en
                 5h/Q21). Le graphe est dessiné comme partout ailleurs ; cette
                 phrase, SOUS LE TITRE, dit ce que ses axes vides ne disent
                 pas : jusqu'à quand cette famille a servi. -->
            <span
                v-if="carteSansValeur && !echecDeLecture && !chargement"
                class="text-color text-xs"
                data-testid="aucune-valeur-servie"
                >{{ phraseSansValeur }}</span
            >
        </template>

        <template #footer>
            <!-- LES TOTAUX, EN TÊTE DU PIED : ce que la fenêtre regardée
                 représente, série par série, dans l'unité du graphe. Le
                 composant de légende chiffrée de l'application, tel quel : même
                 filet de couleur, même typographie que sur la page standard. Il
                 ne rend rien quand il n'y a rien à totaliser. -->
            <!-- La largeur est BORNÉE : le bloc répartit ses lignes sur toute
                 la place qu'on lui donne, et sur une carte pleine largeur deux
                 totaux se retrouvaient collés aux deux bords opposés, à mille
                 pixels l'un de l'autre. Ils se lisent ensemble, ils restent
                 ensemble. -->
            <div v-if="totaux.length" class="max-w-3xl">
                <DataChartLegendKPI
                    data-testid="totaux-de-la-fenetre"
                    title="Totaux sur la fenêtre regardée"
                    title-tag="h3"
                    :items="totaux"
                />
            </div>

            <!-- Le pied ne répète PAS la légende : il ne porte que ce que la
                 légende ne peut pas dire — une origine partagée, une précision
                 sur la grandeur, une série que le compteur ne publie pas. -->
            <ul
                class="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-color m-0 list-none p-0"
                aria-label="Précisions sur les séries"
            >
                <li
                    v-for="serie in seriesAPreciser"
                    :key="serie.cle"
                    class="inline-flex flex-wrap items-center gap-1.5"
                    :data-serie="serie.cle"
                >
                    <span class="font-medium">{{ serie.libelle }}</span>
                    <DataOrigineBadge
                        v-for="part in partsDeLaSerie(serie)"
                        :key="`${serie.cle}-${part.origine}`"
                        :origine="part.origine"
                        :libelle="motDeLaPart(serie, part)"
                    />
                    <span
                        v-if="serie.mention"
                        class="italic"
                        data-testid="precision-de-la-serie"
                        >{{ serie.mention }}</span
                    >
                    <DataOrigineBadge
                        v-if="serie.nonPubliee"
                        origine="absente"
                        :libelle="MENTION_NON_PUBLIEE"
                        data-testid="non-publie"
                    />
                    <!-- LA SÉRIE VIDE SUR LA FENÊTRE, dite là où sa courbe
                         aurait été. Même pastille d'absence que « Non publié »,
                         mot différent : le compteur publie cette grandeur, c'est
                         la fenêtre qui n'en porte aucune mesure. -->
                    <DataOrigineBadge
                        v-else-if="estVide(serie)"
                        origine="absente"
                        :libelle="MENTION_AUCUNE_MESURE"
                        data-testid="aucune-mesure"
                    />
                </li>
                <li
                    v-for="mention in graphe.plafonnees"
                    :key="mention.cle"
                    :data-serie="mention.cle"
                    data-testid="enveloppe-plafonnee"
                    class="inline-flex flex-wrap items-center gap-1.5"
                >
                    <span class="font-medium">{{ mention.libelle }}</span>
                    <span class="italic">{{ mention.texte }}</span>
                </li>
            </ul>

            <!-- LA LÉGENDE DES BANDES, à côté de celle des séries et jamais à
                 la place : les mots et les couleurs sont ceux de la barre de
                 complétude, au pixel près. Elle n'apparaît que si une bande est
                 peinte, et elle ne liste que les états réellement présents. -->
            <DataCompletudeLegend
                v-if="etatsPeints.length && figureRemise"
                :etats="etatsPeints"
                :mots-contrastes="true"
                data-testid="legende-des-trous"
            />

            <!-- On ne sait pas, donc on ne peint pas — ET ON LE DIT. Sans cette
                 ligne, une figure blanche sur un contexte trop long se lirait
                 comme une figure sans trou. Ce n'est pas un message d'attente :
                 c'est la limite de ce que l'écran sait détailler. -->
            <p
                v-if="contexteNonDetaillable"
                class="text-color m-0 text-xs"
                data-testid="contexte-non-detaillable"
            >
                Contexte trop long pour détailler les jours.
            </p>

            <!-- L'ÉTAT SANS COURBE, dit DANS la carte et en TROIS phrases qui
                 ne se confondent jamais : une grandeur que le compteur ne
                 publie pas, une fenêtre qui n'en porte aucune mesure, et une
                 lecture qui a échoué sont trois faits différents (18ᵉ forme de
                 garde qui ment). Pas de cadre, pas d'icône d'alerte, pas de
                 bannière. -->
            <p
                v-if="
                    (sansCourbe || echecDeLecture) &&
                    !chargement &&
                    !(carteSansValeur && !echecDeLecture)
                "
                class="m-0 flex min-h-[4rem] items-center justify-center text-sm text-color"
                :data-testid="
                    echecDeLecture
                        ? 'lecture-impossible'
                        : aucunePeriodeClose
                          ? 'aucune-periode-close'
                          : aucuneMesureSurLaFenetre
                            ? 'aucune-mesure-sur-la-fenetre'
                            : 'grandeur-non-publiee'
                "
                role="status"
            >
                {{
                    echecDeLecture
                        ? "Lecture impossible pour le moment."
                        : aucunePeriodeClose
                          ? PHRASE_AUCUNE_PERIODE
                          : aucuneMesureSurLaFenetre
                            ? PHRASE_AUCUNE_MESURE
                            : "Ce compteur ne publie pas cette grandeur."
                }}
            </p>

            <!-- Les tableaux de valeurs, posés par la vue : ils appartiennent à
                 la carte du graphe, pas à la page.

                 Le pied de la carte pousse TOUT bouton à droite
                 (`[&_.p-button]:ml-auto`), règle écrite pour son unique bouton
                 d'action. La bascule « Valeurs » y tombait aussi : rejetée
                 contre le bord droit de la carte, rétrécie, son libellé coupé
                 en deux. On la ramène ici au bord gauche, là où elle commande
                 le tableau qui la suit. -->
            <div class="flex flex-col gap-4 [&_.p-button]:ml-0!">
                <slot />
            </div>
        </template>
    </PlotlyChartCard>
</template>
